import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaperFigure } from '@conference/shared';
import type { AuthSessionSnapshot } from '../context/AppContext';

type Status = 'idle' | 'loading' | 'ready' | 'error' | 'paused';
type Entry = { figure: PaperFigure; status: Status; url?: string; loadedAt?: number };
const MAX_LOADED_IMAGES = 8;
const MAX_IMAGE_BYTES = 1024 * 1024;
interface Options {
  paperId: string;
  eventId: string;
  figures: PaperFigure[];
  expanded: boolean;
  authSession: AuthSessionSnapshot | null;
  isAuthSessionCurrent: (session: AuthSessionSnapshot) => boolean;
  loadFigure: (figure: PaperFigure, signal: AbortSignal) => Promise<Blob>;
}
interface Run {
  key: string;
  owner: AuthSessionSnapshot;
  active: boolean;
  controller: AbortController;
  entries: Map<string, Entry>;
  visible: Set<string>;
  nearbyNodes: Set<Element>;
  observer?: IntersectionObserver;
  pump: () => void;
}

/** URLs belong to one expanded paper/account view, never a global cache. */
export function usePaperFigures(options: Options) {
  const { paperId, eventId, figures, expanded, authSession } = options;
  const latest = useRef(options);
  latest.current = options;
  const run = useRef<Run | null>(null);
  const nodes = useRef(new Map<Element, string>());
  const [, update] = useState(0);
  const bump = useCallback(() => update(value => value + 1), []);
  const key = JSON.stringify([paperId, eventId, figures.map(f => [f.generation_id, f.asset_id])]);
  const latestKey = useRef(key);
  latestKey.current = key;

  useEffect(() => {
    if (!expanded || !authSession || authSession.signal.aborted
        || !latest.current.isAuthSessionCurrent(authSession)) return;
    const scope: Run = { key, owner: authSession, active: true, controller: new AbortController(),
      entries: new Map(figures.map(f => [f.asset_id, { figure: f, status: 'idle' }])),
      visible: new Set(), nearbyNodes: new Set(), pump: () => {} };
    run.current = scope;
    let activeLoads = 0;
    let sequence = 0;
    const owns = () => scope.active && !scope.controller.signal.aborted && !scope.owner.signal.aborted
      && run.current === scope && latestKey.current === scope.key && latest.current.expanded
      && latest.current.authSession?.userId === scope.owner.userId
      && latest.current.authSession?.generation === scope.owner.generation
      && latest.current.authSession?.signal === scope.owner.signal
      && latest.current.isAuthSessionCurrent(scope.owner);
    scope.pump = () => {
      if (!owns()) return;
      for (const [assetId, entry] of scope.entries) {
        if (activeLoads >= 2) break;
        if (entry.status !== 'idle' || !scope.visible.has(assetId)) continue;
        entry.status = 'loading'; activeLoads++; bump();
        // Catch synchronous loader failures too; no error text reaches the UI.
        Promise.resolve().then(() => {
          if (!owns()) throw new DOMException('Aborted', 'AbortError');
          return latest.current.loadFigure(entry.figure, scope.controller.signal);
        }).then(blob => {
          if (!owns()) return;
          if (blob.size > MAX_IMAGE_BYTES) throw new Error('Image exceeds display limit');
          const ready = [...scope.entries.entries()].filter(([, item]) => item.status === 'ready')
            .sort(([a, one], [b, two]) => Number(scope.visible.has(a)) - Number(scope.visible.has(b))
              || (one.loadedAt ?? 0) - (two.loadedAt ?? 0));
          while (ready.length >= MAX_LOADED_IMAGES) {
            const [oldId, old] = ready.shift()!;
            if (old.url) URL.revokeObjectURL(old.url);
            old.url = undefined; old.status = 'paused'; scope.visible.delete(oldId);
          }
          entry.url = URL.createObjectURL(blob);
          entry.loadedAt = ++sequence;
          entry.status = 'ready';
        }).catch(() => { if (owns()) entry.status = 'error'; })
          .finally(() => { activeLoads--; if (owns()) { bump(); scope.pump(); } });
      }
    };
    const dispose = () => {
      if (!scope.active) return;
      scope.active = false;
      scope.controller.abort();
      scope.observer?.disconnect();
      for (const entry of scope.entries.values()) if (entry.url) URL.revokeObjectURL(entry.url);
      if (run.current === scope) run.current = null;
    };
    const endSession = () => { dispose(); bump(); };
    authSession.signal.addEventListener('abort', endSession, { once: true });
    if (typeof IntersectionObserver === 'undefined') {
      scope.visible = new Set(scope.entries.keys());
    } else {
      scope.observer = new IntersectionObserver(entries => {
        if (!owns()) return;
        for (const entry of entries) {
          const asset = nodes.current.get(entry.target);
          if (asset && entry.isIntersecting) {
            scope.nearbyNodes.add(entry.target);
            scope.visible.add(asset);
            const item = scope.entries.get(asset);
            if (item?.status === 'paused') item.status = 'idle';
          } else if (asset) {
            scope.nearbyNodes.delete(entry.target);
            if (![...scope.nearbyNodes].some(node => nodes.current.get(node) === asset)) scope.visible.delete(asset);
          }
        }
        scope.pump();
      }, { rootMargin: '300px' });
      for (const node of nodes.current.keys()) scope.observer.observe(node);
    }
    bump(); scope.pump();
    return () => { authSession.signal.removeEventListener('abort', endSession); dispose(); };
  // Changes to callbacks or equivalent session object wrappers do not reset a run.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, expanded, authSession?.userId, authSession?.generation, authSession?.signal, bump]);

  const observe = useCallback((assetId: string, node: Element | null) => {
    // Cards call this through a stable ref; node identity permits duplicate assets.
    if (node) { nodes.current.set(node, assetId); run.current?.observer?.observe(node); }
  }, []);
  const forget = useCallback((node: Element) => {
    const scope = run.current, asset = nodes.current.get(node);
    scope?.observer?.unobserve(node); scope?.nearbyNodes.delete(node); nodes.current.delete(node);
    if (scope && asset && ![...scope.nearbyNodes].some(other => nodes.current.get(other) === asset)) scope.visible.delete(asset);
  }, []);
  const retry = useCallback((assetId: string) => {
    const scope = run.current, entry = scope?.entries.get(assetId);
    if (scope?.active && (entry?.status === 'error' || entry?.status === 'paused')) { entry.status = 'idle'; scope.visible.add(assetId); scope.pump(); }
  }, []);
  const fail = useCallback((assetId: string) => {
    const entry = run.current?.entries.get(assetId);
    if (entry?.status === 'ready') {
      if (entry.url) URL.revokeObjectURL(entry.url);
      entry.url = undefined; entry.status = 'error'; bump();
    }
  }, [bump]);
  const scope = run.current;
  const owned = expanded && scope?.active && scope.key === key && !!authSession
    && scope.owner.userId === authSession.userId && scope.owner.generation === authSession.generation
    && scope.owner.signal === authSession.signal && !authSession.signal.aborted
    && options.isAuthSessionCurrent(authSession);
  return { items: figures.map(figure => {
    const entry = owned ? scope.entries.get(figure.asset_id) : undefined;
    return { figure, status: entry?.status ?? 'idle', url: entry?.url };
  }), retry, fail, observe, forget };
}
